import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import {
	FiAlertCircle,
	FiArrowLeft,
	FiCheckCircle,
	FiClock,
	FiEdit,
	FiLink,
	FiMapPin,
	FiPackage,
	FiTrash2,
} from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Header, Sidebar } from '../components/Layout';
import { SidebarProvider } from '../components/SidebarContext';
import { useCreateItemReturn, useDeleteFoundItem, useFoundItem } from '../hooks/useFoundItems';

const statusConfig = {
	disponivel: { label: 'A procurar', icon: FiAlertCircle, color: 'text-amber-600 bg-amber-50' },
	devolvido: { label: 'Devolvido', icon: FiCheckCircle, color: 'text-green-600 bg-green-50' },
} as const;

export function ItemDetailPage() {
	const { id } = useParams<{ id: string }>();
	const { data: item, isLoading, isError, refetch } = useFoundItem(id ?? '');
	const navigate = useNavigate();
	const deleteMutation = useDeleteFoundItem();
	const createReturnMutation = useCreateItemReturn();
	const [copied, setCopied] = useState(false);
	const [showReturnForm, setShowReturnForm] = useState(false);
	const [returnObservation, setReturnObservation] = useState('');

	if (isLoading) return <PageSkeleton />;
	if (isError || !item) return <ErrorState message="Item não encontrado" onRetry={refetch} />;

	const status = statusConfig[item.status];
	const StatusIcon = status.icon;

	return (
		<SidebarProvider>
			<Sidebar />
			<div className="lg:ml-64 min-h-screen bg-gray-50">
				<Header />

				<div className="max-w-4xl mx-auto px-4 py-8">
					<div className="flex items-center gap-4 mb-6">
						<Link
							to="/"
							className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
							aria-label="Voltar"
						>
							<FiArrowLeft size={24} />
						</Link>
						<h1 className="text-2xl font-bold text-gray-900">{item.title}</h1>
					</div>

					<div className="grid lg:grid-cols-3 gap-6">
						<div className="lg:col-span-2 space-y-6">
							<PhotoSection photoUrl={item.photoUrl} title={item.title} />

							{item.description && (
								<section className="p-6 bg-white rounded-lg border border-gray-200">
									<h2 className="text-lg font-semibold text-gray-900 mb-3">Descrição</h2>
									<p className="text-gray-600 whitespace-pre-wrap">{item.description}</p>
								</section>
							)}

							<section className="p-6 bg-white rounded-lg border border-gray-200">
								<h2 className="text-lg font-semibold text-gray-900 mb-4">Informações</h2>
								<dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div>
										<dt className="text-sm text-gray-500">Categoria</dt>
										<dd className="mt-1 font-medium text-gray-900">{item.category?.name ?? '—'}</dd>
									</div>
									<div>
										<dt className="text-sm text-gray-500">Status</dt>
										<dd className="mt-1">
											<span
												className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${status.color}`}
											>
												<StatusIcon size={14} aria-hidden="true" />
												{status.label}
											</span>
										</dd>
									</div>
									<div className="sm:col-span-2">
										<dt className="text-sm text-gray-500">Local encontrado</dt>
										<dd className="mt-1 flex items-center gap-2 text-gray-900">
											<FiMapPin size={18} className="text-gray-400" aria-hidden="true" />
											{item.foundLocationText}
										</dd>
									</div>
									{item.foundLatitude !== null && item.foundLongitude !== null && (
										<div className="sm:col-span-2">
											<dt className="text-sm text-gray-500">Coordenadas</dt>
											<dd className="mt-1 font-mono text-sm text-gray-900">
												{item.foundLatitude.toFixed(6)}, {item.foundLongitude.toFixed(6)}
											</dd>
										</div>
									)}
									<div>
										<dt className="text-sm text-gray-500">Encontrado em</dt>
										<dd className="mt-1 text-gray-900">
											{format(new Date(item.foundAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
										</dd>
									</div>
									<div>
										<dt className="text-sm text-gray-500">Cadastrado em</dt>
										<dd className="mt-1 text-gray-900">
											{format(new Date(item.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
										</dd>
									</div>
								</dl>
							</section>

							{item.status === 'devolvido' && item.itemReturn && (
								<section className="p-6 bg-white rounded-lg border border-gray-200 bg-green-50">
									<h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
										<FiCheckCircle size={20} className="text-green-600" />
										Devolução
									</h2>
									<dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										<div>
											<dt className="text-sm text-gray-500">Devolvido em</dt>
											<dd className="mt-1 text-gray-900">
												{format(new Date(item.itemReturn.returnedAt), "dd/MM/yyyy 'às' HH:mm", {
													locale: ptBR,
												})}
											</dd>
										</div>
										{item.itemReturn.observation && (
											<div className="sm:col-span-2">
												<dt className="text-sm text-gray-500">Observação</dt>
												<dd className="mt-1 text-gray-900">{item.itemReturn.observation}</dd>
											</div>
										)}
									</dl>
								</section>
							)}
						</div>

						<aside className="space-y-4">
							<div className="p-6 bg-white rounded-lg border border-gray-200">
								<h3 className="font-semibold text-gray-900 mb-4">Ações</h3>
								<div className="space-y-2">
									{item.status === 'disponivel' && (
										<>
											{!showReturnForm ? (
												<button
													onClick={() => setShowReturnForm(true)}
													className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 text-green-600 font-medium hover:bg-green-100"
												>
													<FiCheckCircle size={18} />
													Marcar como devolvido
												</button>
											) : (
												<div className="space-y-2 p-3 bg-green-50 rounded-lg border border-green-200">
													<p className="text-sm font-medium text-green-800">
														Observação (opcional)
													</p>
													<textarea
														value={returnObservation}
														onChange={(e) => setReturnObservation(e.target.value)}
														className="w-full px-3 py-2 text-sm border border-green-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
														rows={2}
														placeholder="Ex: entregue ao proprietário..."
													/>
													<div className="flex gap-2">
														<button
															onClick={() => {
																createReturnMutation.mutate(
																	{ itemId: item.id, observation: returnObservation || undefined },
																	{
																		onSuccess: () => {
																			setShowReturnForm(false);
																			setReturnObservation('');
																		},
																	},
																);
															}}
															disabled={createReturnMutation.isPending}
															className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
														>
															{createReturnMutation.isPending ? 'Salvando...' : 'Confirmar'}
														</button>
														<button
															onClick={() => {
																setShowReturnForm(false);
																setReturnObservation('');
															}}
															className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
														>
															Cancelar
														</button>
													</div>
												</div>
											)}
										</>
									)}
									<Link
										to={`/items/${item.id}/edit`}
										className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
									>
										<FiEdit size={18} />
										Editar
									</Link>
									<button
										onClick={() => {
											if (window.confirm('Excluir este item? Essa ação não pode ser desfeita.')) {
												deleteMutation.mutate(item.id, {
													onSuccess: () => navigate('/'),
												});
											}
										}}
										disabled={deleteMutation.isPending}
										className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-red-50 text-red-600 font-medium hover:bg-red-100 disabled:opacity-50"
									>
										<FiTrash2 size={18} />
										{deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
									</button>
								</div>
							</div>

							<div className="p-6 bg-white rounded-lg border border-gray-200">
								<h3 className="font-semibold text-gray-900 mb-4">Compartilhar</h3>
								<button
									onClick={() => {
										navigator.clipboard.writeText(window.location.href);
										setCopied(true);
										setTimeout(() => setCopied(false), 2000);
									}}
									className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
								>
									<FiLink size={18} />
									{copied ? 'Copiado!' : 'Copiar link'}
								</button>
							</div>
						</aside>
					</div>
				</div>
			</div>
		</SidebarProvider>
	);
}

function PhotoSection({ photoUrl, title }: { photoUrl: string; title: string }) {
	return (
		<div className="relative aspect-video w-full bg-gray-100 rounded-xl overflow-hidden">
			{photoUrl ? (
				<img src={photoUrl} alt={title} className="w-full h-full object-cover" />
			) : (
				<div className="w-full h-full flex items-center justify-center">
					<FiPackage size={64} className="text-gray-300" />
				</div>
			)}
		</div>
	);
}

function PageSkeleton() {
	return (
		<div className="max-w-4xl mx-auto px-4 py-8 space-y-6 animate-pulse">
			<div className="h-8 bg-gray-200 rounded w-1/4" />
			<div className="aspect-video bg-gray-200 rounded-xl" />
			<div className="space-y-4">
				<div className="h-6 bg-gray-200 rounded w-1/3" />
				<div className="h-4 bg-gray-200 rounded w-full" />
				<div className="h-4 bg-gray-200 rounded w-2/3" />
			</div>
		</div>
	);
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
	return (
		<div className="max-w-4xl mx-auto px-4 py-16 text-center">
			<FiAlertCircle size={48} className="text-gray-300 mx-auto mb-4" />
			<h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
			<button
				onClick={onRetry}
				className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
			>
				Tentar novamente
			</button>
		</div>
	);
}
